import { platformApi } from '@/lib/platform-api';
import type { AlertFilters, AlertResolutionType, TransactionAlert } from '../types/alerts';

function mapAlertRow(row: Record<string, unknown>): TransactionAlert {
  const caseData = row.case as Record<string, unknown> | null | undefined;
  return {
    id: row.id as string,
    case_id: row.case_id as string,
    transaction_id: (row.transaction_id as string | null) ?? null,
    operator_id: (row.operator_id as string) ?? '',
    alert_type: row.alert_type as TransactionAlert['alert_type'],
    severity: row.severity as TransactionAlert['severity'],
    status: row.status as TransactionAlert['status'],
    details: (row.details as Record<string, unknown>) ?? {},
    acknowledged_by: (row.acknowledged_by as string | null) ?? null,
    acknowledged_at: (row.acknowledged_at as string | null) ?? null,
    resolved_by: (row.resolved_by as string | null) ?? null,
    resolved_at: (row.resolved_at as string | null) ?? null,
    resolution_type: (row.resolution_type as TransactionAlert['resolution_type']) ?? null,
    resolution_notes: (row.resolution_notes as string | null) ?? null,
    idempotency_key: (row.idempotency_key as string) ?? '',
    created_at: row.created_at as string,
    updated_at: (row.updated_at as string) ?? '',
    external_case_ref: (caseData?.external_case_ref as string | null) ?? null,
  };
}

export async function fetchAlerts(
  filters: AlertFilters
): Promise<{ alerts: TransactionAlert[]; count: number; hasMore: boolean; nextCursor: string | null }> {
  const { pageSize, status, alertType, severity, dateFrom, dateTo, caseId, cursor } = filters;

  const { data, error } = await platformApi.GET('/platform/v1/finance/alerts', {
    params: {
      query: {
        limit: pageSize,
        'expand[]': ['case'],
        ...(cursor ? { cursor } : {}),
        ...(status ? { status } : {}),
        ...(alertType ? { alert_type: alertType } : {}),
        ...(severity ? { severity } : {}),
        ...(dateFrom ? { created_after: dateFrom } : {}),
        ...(dateTo ? { created_before: `${dateTo}T23:59:59.999Z` } : {}),
        ...(caseId ? { case_id: caseId } : {}),
        ...(filters.clientId ? { client_id: filters.clientId } : {}),
      },
    },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch alerts';
    throw new Error(String(msg));
  }

  const envelope = data as {
    data: Record<string, unknown>[];
    has_more: boolean;
    next_cursor?: string | null;
  };

  const alerts: TransactionAlert[] = envelope.data.map((row) => mapAlertRow(row));

  return {
    alerts,
    count: alerts.length,
    hasMore: envelope.has_more,
    nextCursor: envelope.next_cursor ?? null,
  };
}

export interface PendingAlertsCount {
  /** Number of pending alerts, capped by the backend (see `hasMore`). */
  pending: number;
  /** True when the true count exceeds the cap — display `pending` as a lower bound (e.g. "100+"). */
  hasMore: boolean;
}

export async function fetchPendingAlertsCount(clientId?: string | null): Promise<PendingAlertsCount> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/alerts/summary', {
    ...(clientId ? { params: { query: { client_id: clientId } } } : {}),
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch pending alerts count';
    throw new Error(String(msg));
  }

  const payload = data.data as { pending?: number; has_more?: boolean };
  return {
    pending: Number(payload.pending ?? 0),
    hasMore: payload.has_more ?? false,
  };
}

export async function acknowledgeAlert(alertId: string): Promise<void> {
  const { error } = await platformApi.PATCH('/platform/v1/finance/alerts/{alert_id}', {
    params: { path: { alert_id: alertId } },
  });

  if (error) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to acknowledge alert';
    throw new Error(String(msg));
  }
}

export async function resolveAlert(alertId: string, resolutionType: AlertResolutionType, notes?: string): Promise<void> {
  const { error } = await platformApi.POST('/platform/v1/finance/alerts/{alert_id}/actions/resolve', {
    params: { path: { alert_id: alertId } },
    body: {
      resolution_type: resolutionType,
      resolution_notes: notes ?? null,
    },
  });

  if (error) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to resolve alert';
    throw new Error(String(msg));
  }
}

export async function dismissAlert(alertId: string): Promise<void> {
  await resolveAlert(alertId, 'false_positive');
}

export interface TransactionLogEntry {
  id: string;
  transaction_id: string;
  status: string;
  error_message: string | null;
  retry_count: number | null;
  executed_at: string | null;
  created_at: string | null;
  metadata: Record<string, unknown> | null;
}

export async function fetchTransactionLogs(transactionId: string): Promise<TransactionLogEntry[]> {
  const { data, error } = await platformApi.GET('/platform/v1/finance/transactions/{transaction_id}/logs', {
    params: { path: { transaction_id: transactionId } },
  });

  if (error || !data) {
    const msg = (error as Record<string, unknown> | undefined)?.detail ?? 'Failed to fetch transaction logs';
    throw new Error(String(msg));
  }

  const payload = data as { data: TransactionLogEntry[] };
  return payload.data ?? [];
}
